import { ApiService } from './api';
import {
  CreateAttributionTagRequest,
  UpdateAttributionTagRequest,
  TagPostsQuery,
  TagMetricsQuery,
  DiscoveredHashtagsQuery,
  UnifiedHashtagsQuery,
  HashtagQuery,
  HashtagPostsQuery,
  PromoteHashtagRequest,
  AttributionTagResponse,
  AttributionTagDetailResponse,
  UnifiedHashtagDetailResponse,
  TagPostsResponse,
  TagMetricsResponse,
  DiscoveredHashtagsResponse,
  HashtagDetailResponse,
  HashtagPostsResponse,
  TagPickerResponse,
  PeriodType,
  AttributionTagStatus,
  TagGroupResponse,
  TagGroupListResponse,
  AttributionListResponse,
} from '@/types/attributionTag';

// CRO API URL Constants
const CRO_API_URLS = {
  local: 'http://localhost:3007/api/v1',
  remote: 'https://ath-cro-service-uat-west.azurewebsites.net/api/v1'
} as const;

// Get CRO API URL from environment or fall back to local
const getCroApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_CRO_API_URL;
  const mode = import.meta.env.MODE;

  if (envUrl) {
    return envUrl;
  }

  // Fallback based on mode
  if (mode === 'remote') {
    return CRO_API_URLS.remote;
  }

  return CRO_API_URLS.local;
};

// Create a dedicated API service instance for CRO API
class CroApiService extends ApiService {
  constructor() {
    super();
    // Override the baseURL to use CRO API
    (this as any).baseURL = getCroApiUrl();
  }
}

// CRO API service instance
const croApiService = new CroApiService();

// Response type for list endpoint
interface HashtagsListResponse {
  success: boolean;
  data: Array<{
    hashtag_id: number;
    hashtag_text: string;
    description: string | null;
    status: string | null;
    created_at: string | null;
  }>;
  total: number;
}

export class AttributionTagService {
  // ==========================================
  // CRUD Operations
  // ==========================================

  /**
   * Get hashtags list (no period filtering, alphabetical order)
   * Used for the Attribution Tags and Discovered Hashtags panel display
   * @param customerId - Customer UUID
   * @param type - 'attribution' (tracked) or 'discovered' (untracked)
   */
  async getHashtagsList(
    customerId: string,
    type: 'attribution' | 'discovered' = 'attribution'
  ): Promise<HashtagsListResponse> {
    return croApiService.get<HashtagsListResponse>('/attribution-tags', {
      customerId,
      type,
    } as Record<string, unknown>);
  }

  /**
   * @deprecated Use getHashtagsList instead
   */
  async getTagsList(customerId: string): Promise<HashtagsListResponse> {
    return this.getHashtagsList(customerId, 'attribution');
  }

  /**
   * Create a new attribution tag
   * customerId is passed in request body
   */
  async createTag(data: CreateAttributionTagRequest): Promise<AttributionTagResponse> {
    return croApiService.post<AttributionTagResponse>('/attribution-tags', data);
  }

  /**
   * Get active tags for picker/dropdown components
   * customerId is required in query params
   */
  async getTagsForPicker(customerId: string): Promise<TagPickerResponse> {
    return croApiService.get<TagPickerResponse>('/attribution-tags/picker', {
      customerId,
    } as Record<string, unknown>);
  }

  /**
   * 🆕 UNIFIED ENDPOINT - Get complete hashtag details with metrics and accounts
   * Works for both attribution tags (is_tracked=true) and discovered hashtags (is_tracked=false)
   * customerId is required in query params
   *
   * @param hashtagId - Numeric hashtag identifier
   * @param customerId - Customer UUID
   * @param query - Optional period/date filters for metrics
   */
  async getHashtagById(
    hashtagId: number | string,
    customerId: string,
    query?: HashtagQuery
  ): Promise<UnifiedHashtagDetailResponse> {
    return croApiService.get<UnifiedHashtagDetailResponse>(`/hashtags/${hashtagId}`, {
      customerId,
      ...query,
    } as Record<string, unknown>);
  }

  /**
   * Get basic hashtag details + linked accounts (no metrics)
   * Designed for the edit view — avoids the 404 issue when a hashtag has no posts in the current period
   *
   * @param hashtagId - Numeric hashtag identifier
   * @param customerId - Customer UUID
   */
  async getHashtagBasicById(
    hashtagId: number | string,
    customerId: string
  ): Promise<UnifiedHashtagDetailResponse> {
    return croApiService.get<UnifiedHashtagDetailResponse>(`/hashtags/${hashtagId}/basic`, {
      customerId,
    } as Record<string, unknown>);
  }

  /**
   * @deprecated Use getHashtagById instead - this is the old attribution-tags-only endpoint
   * Get single tag with metrics and accounts
   * customerId is required in query params
   */
  async getTagById(tagId: string, customerId: string): Promise<AttributionTagDetailResponse> {
    return croApiService.get<AttributionTagDetailResponse>(`/attribution-tags/${tagId}`, {
      customerId,
    } as Record<string, unknown>);
  }

  /**
   * Update attribution tag
   * customerId is passed in query string
   */
  async updateTag(
    tagId: string,
    customerId: string,
    data: UpdateAttributionTagRequest
  ): Promise<AttributionTagResponse> {
    return croApiService.patch<AttributionTagResponse>(
      `/attribution-tags/${tagId}?customerId=${customerId}`,
      data
    );
  }

  /**
   * Delete (retire) attribution tag
   * customerId is passed in query string
   */
  async deleteTag(tagId: string, customerId: string): Promise<void> {
    await croApiService.delete(`/attribution-tags/${tagId}?customerId=${customerId}`);
  }

  // ==========================================
  // Metrics & Content
  // ==========================================

  /**
   * Get aggregated metrics for a tag
   * customerId is required in query params
   */
  async getTagMetrics(
    tagId: string,
    customerId: string,
    query?: TagMetricsQuery
  ): Promise<TagMetricsResponse> {
    return croApiService.get<TagMetricsResponse>(
      `/attribution-tags/${tagId}/metrics`,
      { customerId, ...query } as Record<string, unknown>
    );
  }

  /**
   * Get posts attributed to a tag
   * customerId is required in query params
   */
  async getTagPosts(
    tagId: string,
    customerId: string,
    query?: TagPostsQuery
  ): Promise<TagPostsResponse> {
    return croApiService.get<TagPostsResponse>(
      `/attribution-tags/${tagId}/posts`,
      { customerId, ...query } as Record<string, unknown>
    );
  }

  // ==========================================
  // Discovered Hashtags
  // ==========================================

  /**
   * Get discovered hashtags (not yet promoted)
   * customerId is required in query params
   */
  async getDiscoveredHashtags(
    customerId: string,
    query?: DiscoveredHashtagsQuery
  ): Promise<DiscoveredHashtagsResponse> {
    return croApiService.get<DiscoveredHashtagsResponse>(
      '/hashtags',
      { customerId, type: 'discovered', ...query } as Record<string, unknown>
    );
  }

  /**
   * Promote a discovered hashtag to an attribution tag
   * customerId is passed in request body
   */
  async promoteHashtag(data: PromoteHashtagRequest): Promise<AttributionTagResponse> {
    return croApiService.post<AttributionTagResponse>('/attribution-tags/promote', data);
  }

  /**
   * 🆕 Get posts using hashtag ID (numeric)
   * GET /hashtags/:hashtagId/posts
   */
  async getHashtagPostsById(
    hashtagId: number | string,
    customerId: string,
    query?: HashtagPostsQuery
  ): Promise<HashtagPostsResponse> {
    return croApiService.get<HashtagPostsResponse>(
      `/hashtags/${hashtagId}/posts`,
      { customerId, ...query } as Record<string, unknown>
    );
  }

  /**
   * @deprecated Use getHashtagById instead - this endpoint uses text-based lookup
   * Get single hashtag details with full metrics
   * GET /hashtags/:hashtagText
   */
  async getHashtagDetail(
    hashtagText: string,
    customerId: string,
    query?: HashtagQuery
  ): Promise<HashtagDetailResponse> {
    return croApiService.get<HashtagDetailResponse>(
      `/hashtags/${encodeURIComponent(hashtagText)}`,
      { customerId, ...query } as Record<string, unknown>
    );
  }

  /**
   * @deprecated Use getHashtagPostsById instead - this endpoint uses text-based lookup
   * Get posts using a specific hashtag
   * GET /hashtags/:hashtagText/posts
   */
  async getHashtagPosts(
    hashtagText: string,
    customerId: string,
    query?: HashtagPostsQuery
  ): Promise<HashtagPostsResponse> {
    return croApiService.get<HashtagPostsResponse>(
      `/hashtags/${encodeURIComponent(hashtagText)}/posts`,
      { customerId, ...query } as Record<string, unknown>
    );
  }

  // ==========================================
  // All Hashtags (Combined View - uses is_tracked flag)
  // ==========================================

  /**
   * Get all hashtags (both attribution tags and discovered)
   * customerId is required in query params
   * type, status, and other filters are passed as query params
   */
  async getAllHashtags(
    customerId: string,
    query?: UnifiedHashtagsQuery
  ): Promise<DiscoveredHashtagsResponse> {
    const apiQuery: Record<string, unknown> = {
      customerId,
      type: query?.type,
      search: query?.search,
      // Status filter only applies to attribution tags
      status: query?.type === 'attribution' ? query?.status : undefined,
      sort_by: query?.sort_by,
      sort_order: query?.sort_order,
      limit: query?.limit,
      offset: query?.offset,
      period: query?.period,
      start_date: query?.start_date,
      end_date: query?.end_date,
    };

    return croApiService.get<DiscoveredHashtagsResponse>(
      '/hashtags',
      apiQuery
    );
  }

  // ==========================================
  // Attribution list (grouped/solo entries, canonical deduped metrics)
  // ==========================================

  /**
   * The attribution tags list with metrics — the single source for the attribution page.
   * Returns a mix of GROUP entries (collapsed variants) and solo TAG entries.
   * Metrics come from the canonical override-aware, group-deduped view (CRO).
   */
  async getAttributionList(
    customerId: string,
    query?: {
      search?: string;
      status?: AttributionTagStatus;
      sort_by?: 'emv' | 'posts' | 'recent';
      sort_order?: 'asc' | 'desc';
      period?: PeriodType;
      customStartDate?: string;
      customEndDate?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<AttributionListResponse> {
    return croApiService.get<AttributionListResponse>('/hashtags', {
      customerId,
      type: 'attribution',
      search: query?.search,
      status: query?.status,
      sort_by: query?.sort_by,
      sort_order: query?.sort_order,
      period: query?.period,
      customStartDate: query?.customStartDate,
      customEndDate: query?.customEndDate,
      limit: query?.limit,
      offset: query?.offset,
    });
  }

  // ==========================================
  // Tag Grouping
  // ==========================================

  /** Create a group with optional initial member tags (hashtag_ids). */
  async createTagGroup(
    customerId: string,
    name: string,
    tagIds: number[]
  ): Promise<TagGroupResponse> {
    return croApiService.post<TagGroupResponse>('/attribution-tags/groups', {
      customerId,
      name,
      tag_ids: tagIds,
    });
  }

  /** List all groups (with members) for a customer. */
  async listTagGroups(customerId: string): Promise<TagGroupListResponse> {
    return croApiService.get<TagGroupListResponse>('/attribution-tags/groups', {
      customerId,
    });
  }

  /** Group detail + members + aggregate metrics (period-aware). */
  async getTagGroup(
    groupId: string,
    customerId: string,
    query?: { period?: PeriodType; customStartDate?: string; customEndDate?: string }
  ): Promise<TagGroupResponse> {
    return croApiService.get<TagGroupResponse>(`/attribution-tags/groups/${groupId}`, {
      customerId,
      period: query?.period,
      customStartDate: query?.customStartDate,
      customEndDate: query?.customEndDate,
    });
  }

  /** Deduped union of a group's posts. */
  async getTagGroupPosts(
    groupId: string,
    customerId: string,
    query?: TagPostsQuery
  ): Promise<TagPostsResponse> {
    return croApiService.get<TagPostsResponse>(`/attribution-tags/groups/${groupId}/posts`, {
      customerId,
      limit: query?.limit,
      offset: query?.offset,
      period: query?.period,
      customStartDate: query?.customStartDate,
      customEndDate: query?.customEndDate,
    });
  }

  /** Rename a group. */
  async renameTagGroup(
    groupId: string,
    customerId: string,
    name: string
  ): Promise<TagGroupResponse> {
    return croApiService.patch<TagGroupResponse>(
      `/attribution-tags/groups/${groupId}?customerId=${customerId}`,
      { name }
    );
  }

  /** Add member tags (hashtag_ids) to a group. */
  async addTagGroupMembers(
    groupId: string,
    customerId: string,
    tagIds: number[]
  ): Promise<TagGroupResponse> {
    return croApiService.post<TagGroupResponse>(
      `/attribution-tags/groups/${groupId}/members?customerId=${customerId}`,
      { tag_ids: tagIds }
    );
  }

  /** Remove member tags (hashtag_ids) from a group; they revert to standalone. */
  async removeTagGroupMembers(
    groupId: string,
    customerId: string,
    tagIds: number[]
  ): Promise<TagGroupResponse> {
    return croApiService.delete<TagGroupResponse>(
      `/attribution-tags/groups/${groupId}/members?customerId=${customerId}`,
      { tag_ids: tagIds }
    );
  }

  /** Delete a group (members revert to standalone tags). */
  async deleteTagGroup(groupId: string, customerId: string): Promise<void> {
    await croApiService.delete(`/attribution-tags/groups/${groupId}?customerId=${customerId}`);
  }

  // ==========================================
  // Activation-related Methods
  // ==========================================

  /**
   * Get activations linked to a hashtag (via assets)
   * GET /hashtags/:hashtagId/activations
   */
  async getHashtagActivations(
    hashtagId: number | string,
    customerId: string
  ): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      name: string;
      description: string | null;
      partner_id: string;
      partner_name: string | null;
      partner_logo_url: string | null;
      activation_type_id: string | null;
      activation_type_name: string | null;
      deal_value: number | null;
      emv_target_value: number | null;
      start_date: string | null;
      end_date: string | null;
      status: string;
      created_at: string | null;
      // EMV metrics from full asset-matching pattern
      total_posts: number;
      total_emv: number;
      total_views: number;
      total_engagement: number;
    }>;
  }> {
    return croApiService.get<{
      success: boolean;
      data: Array<{
        id: string;
        name: string;
        description: string | null;
        partner_id: string;
        partner_name: string | null;
        partner_logo_url: string | null;
        activation_type_id: string | null;
        activation_type_name: string | null;
        deal_value: number | null;
        emv_target_value: number | null;
        start_date: string | null;
        end_date: string | null;
        status: string;
        created_at: string | null;
        // EMV metrics from full asset-matching pattern
        total_posts: number;
        total_emv: number;
        total_views: number;
        total_engagement: number;
      }>;
    }>(`/hashtags/${hashtagId}/activations`, {
      customerId,
    } as Record<string, unknown>);
  }

  /**
   * Get hashtag performance within an activation context
   * GET /hashtags/:hashtagId/activations/:activationId/performance
   */
  async getHashtagActivationPerformance(
    hashtagId: number | string,
    activationId: string,
    customerId: string
  ): Promise<{
    success: boolean;
    data: {
      hashtagId: number;
      hashtagText: string;
      totalPosts: number;
      totalEmv: number;
      totalViews: number;
      totalLikes: number;
      totalComments: number;
      totalShares: number;
      totalSaves: number;
      totalEngagement: number;
      avgEmvPerPost: number;
      avgEngagementPerPost: number;
      dateRange: {
        earliest: string | null;
        latest: string | null;
      };
    };
  }> {
    return croApiService.get<{
      success: boolean;
      data: {
        hashtagId: number;
        hashtagText: string;
        totalPosts: number;
        totalEmv: number;
        totalViews: number;
        totalLikes: number;
        totalComments: number;
        totalShares: number;
        totalSaves: number;
        totalEngagement: number;
        avgEmvPerPost: number;
        avgEngagementPerPost: number;
        dateRange: {
          earliest: string | null;
          latest: string | null;
        };
      };
    }>(`/hashtags/${hashtagId}/activations/${activationId}/performance`, {
      customerId,
    } as Record<string, unknown>);
  }
}

export const attributionTagService = new AttributionTagService();
